import { Button, Checkbox, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { companies, type Company } from "@/api/addressBook.service";
import {
  MOVE_TABLE_KEYS,
  companyContentMoveService,
  type CompanyContentMoveApplyResponse,
  type CompanyContentMoveConflict,
  type CompanyContentMoveValidateResponse,
  type MoveTableKey,
} from "@/api/profiles/companyContentMoveService";
import { CompanyPicker } from "@/components/CompanyPicker/CompanyPicker";
import { ROUTES } from "@/routes";
import styles from "./MoveCompanyContentModal.module.css";

const TABLE_LABELS: Record<MoveTableKey, string> = {
  lawyer_profiles: "Lawyer profiles",
  firm_profiles: "Firm profiles",
  firm_tab_general: "Firm tab: General",
  firm_tab_awards: "Firm tab: Awards",
  firm_tab_client_testimonials: "Firm tab: Client testimonials",
  firm_tab_diversity: "Firm tab: Diversity",
  firm_tab_international_capabilities: "Firm tab: International capabilities",
  firm_tab_multimedia: "Firm tab: Multimedia",
  firm_tab_notable_cases: "Firm tab: Notable cases",
  firm_tab_partner_perspectives: "Firm tab: Partner perspectives",
  firm_tab_sponsorship: "Firm tab: Sponsorship",
  firm_tab_webinar: "Firm tab: Webinar",
  firm_tab_video: "Firm tab: Video",
  team_pages: "Team pages",
  firm_articles: "Articles",
};

/**
 * Resolves a company's display name, tolerating the two states a MERGED-AWAY
 * company id is expected to be in: address-book 404s (unknown id), or 200s
 * dereferenced to the merge SURVIVOR — whose id differs from the requested
 * one and whose name must not be presented as the requested company's. Both
 * resolve to null (caller falls back to the raw uuid); a lookup failure must
 * never block the move flow, which exists precisely to clean up after such
 * dead companies.
 */
async function resolveCompanyName(id: string): Promise<string | null> {
  try {
    const response = await companies.getById(id);
    return response.data.id === id ? response.data.name : null;
  } catch {
    return null;
  }
}

function conflictSlot(conflict: CompanyContentMoveConflict): string {
  if (conflict.kind === "duplicate_page_type_website") {
    return `page type ${conflict.pageType} / website ${conflict.website}`;
  }
  return `language ${conflict.languageCode ?? "(none)"} / website ${conflict.website}`;
}

function nonZeroCounts(counts: Record<MoveTableKey, number>): Array<[MoveTableKey, number]> {
  return MOVE_TABLE_KEYS.filter(key => counts[key] > 0).map(key => [key, counts[key]]);
}

type Step = "pick" | "review" | "result";

export type MoveCompanyContentModalProps = {
  /** The (usually merged-away) company whose content is being moved — fixed by the launching page. */
  sourceCompanyId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Fired once when apply succeeds, so the page can refetch after the modal closes. */
  onApplied?: () => void;
};

/**
 * SOFTSUPP-530 staff tool: move ALL profiles content from one company to
 * another, cleaning up after an address-book merge that left content
 * orphaned on the dead source company. Three steps: pick a target, review
 * the dry run (counts, merge-record checks, per-row conflict decisions),
 * apply and download the rollback manifest.
 */
export function MoveCompanyContentModal({
  sourceCompanyId,
  isOpen,
  onClose,
  onApplied,
}: MoveCompanyContentModalProps) {
  const [step, setStep] = useState<Step>("pick");
  const [pickedCompany, setPickedCompany] = useState<Company | null>(null);
  /** The target validate ran against — the picker's pick, or the canonical suggestion. */
  const [target, setTarget] = useState<{ id: string; name: string | null } | null>(null);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const [canonicalName, setCanonicalName] = useState<string | null>(null);
  const [validation, setValidation] = useState<CompanyContentMoveValidateResponse | null>(null);
  /** sourceRowId -> skip? Conflicts default to true (skip), warnings to false (move). */
  const [skipDecisions, setSkipDecisions] = useState<Record<string, boolean>>({});
  const [unconfirmedAck, setUnconfirmedAck] = useState(false);
  const [busy, setBusy] = useState<"validate" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<CompanyContentMoveApplyResponse | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  // Full reset on every open — a half-reviewed move must never leak into the
  // next one — plus the source-name lookup (uuid fallback, never blocking).
  useEffect(() => {
    if (!isOpen) return;
    setStep("pick");
    setPickedCompany(null);
    setTarget(null);
    setSourceName(null);
    setCanonicalName(null);
    setValidation(null);
    setSkipDecisions({});
    setUnconfirmedAck(false);
    setBusy(null);
    setError(null);
    setApplied(null);

    let active = true;
    void resolveCompanyName(sourceCompanyId).then(name => {
      if (active) setSourceName(name);
    });
    return () => {
      active = false;
    };
  }, [isOpen, sourceCompanyId]);

  // Name for the "use canonical target instead" suggestion (uuid fallback).
  useEffect(() => {
    const canonicalId = validation?.canonicalTargetId;
    if (!isOpen || !canonicalId || validation?.relationshipConfirmed) return;
    let active = true;
    void resolveCompanyName(canonicalId).then(name => {
      if (active) setCanonicalName(name);
    });
    return () => {
      active = false;
    };
  }, [isOpen, validation]);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && busy === null) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, busy, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;
    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  async function runValidate(targetId: string, targetName: string | null) {
    setBusy("validate");
    setError(null);
    try {
      const result = await companyContentMoveService.validate({
        sourceCompanyId,
        targetCompanyId: targetId,
      });
      const decisions: Record<string, boolean> = {};
      // Skip is the recommended default for conflicts: forcing a genuine
      // duplicate slot fails the whole transactional move. Warnings default
      // to move — the same person profiled at two firms is legal.
      for (const warning of result.warnings) decisions[warning.sourceRowId] = false;
      for (const conflict of result.conflicts) decisions[conflict.sourceRowId] = true;
      setTarget({ id: targetId, name: targetName });
      setValidation(result);
      setSkipDecisions(decisions);
      setUnconfirmedAck(false);
      setCanonicalName(null);
      setStep("review");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Validation failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleApply() {
    if (!target) return;
    setBusy("apply");
    setError(null);
    try {
      const skipRowIds = Object.entries(skipDecisions)
        .filter(([, skip]) => skip)
        .map(([id]) => id);
      const result = await companyContentMoveService.apply({
        sourceCompanyId,
        targetCompanyId: target.id,
        ...(skipRowIds.length > 0 ? { skipRowIds } : {}),
      });
      setApplied(result);
      setStep("result");
      onApplied?.();
    } catch (err) {
      // Stay on the review step with every decision intact — the operator
      // fixes the problem (usually un-forcing a conflict) and retries.
      setError(err instanceof Error ? err.message : "Move failed");
    } finally {
      setBusy(null);
    }
  }

  function handleDownloadManifest() {
    if (!applied || !target) return;
    const manifest = {
      sourceCompanyId,
      targetCompanyId: target.id,
      movedIds: applied.movedIds,
      totalMoved: applied.totalMoved,
      appliedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `company-content-move-manifest-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}.json`;
    anchor.click();
    // Deferred: revoking in the same tick can cancel the download before it starts.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && busy === null) {
      onClose();
    }
  }

  const sameAsSource = pickedCompany?.id === sourceCompanyId;
  const sourceLabel = sourceName ?? sourceCompanyId;
  const targetLabel = target ? (target.name ?? target.id) : "";

  const needsAck =
    validation !== null && (!validation.relationshipConfirmed || !validation.targetCompanyAlive);
  const skippedCount = Object.values(skipDecisions).filter(Boolean).length;
  const willMoveCount = validation ? validation.totalToMove - skippedCount : 0;
  const applyDisabled = busy !== null || willMoveCount <= 0 || (needsAck && !unconfirmedAck);

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-company-content-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="move-company-content-title" className={styles.modalTitle}>
            Move all content to another firm
          </h2>
          <button
            type="button"
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={busy !== null}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {step === "pick" && (
            <>
              <p className={styles.intro}>
                Moves every profiles content row (lawyer and firm profiles, firm tabs, team pages,
                articles) from <strong>{sourceLabel}</strong> to the firm you pick. Nothing is
                written until you review the dry run and apply.
              </p>
              <CompanyPicker
                value={pickedCompany}
                onChange={setPickedCompany}
                label="Target company"
                listboxId="move-target-company-listbox"
                error={
                  sameAsSource
                    ? "The target must be a different company than the source."
                    : undefined
                }
              />
            </>
          )}

          {step === "review" && validation && target && (
            <>
              <div className={styles.summaryBox}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Source:</span>
                  <span className={styles.infoValue}>
                    {sourceLabel}
                    {sourceName && <code className={styles.uuid}>{sourceCompanyId}</code>}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Target:</span>
                  <span className={styles.infoValue}>
                    {targetLabel}
                    {target.name && <code className={styles.uuid}>{target.id}</code>}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Rows to move:</span>
                  <span className={styles.infoValue}>
                    {willMoveCount} of {validation.totalToMove}
                    {skippedCount > 0 && ` (${skippedCount} skipped)`}
                  </span>
                </div>
              </div>

              {validation.totalToMove === 0 ? (
                <p className={styles.emptyText}>The source company has no content to move.</p>
              ) : (
                <ul className={styles.countsList} aria-label="Rows to move per table">
                  {nonZeroCounts(validation.counts).map(([key, count]) => (
                    <li key={key} className={styles.countsItem}>
                      <span>{TABLE_LABELS[key]}</span>
                      <span className={styles.countBadge}>{count}</span>
                    </li>
                  ))}
                </ul>
              )}

              {!validation.relationshipConfirmed && (
                <div className={styles.dangerBox} role="alert">
                  <Icon icon="exclamation" size="small" />
                  <div>
                    <strong>
                      The address book does NOT confirm that the source was merged directly into
                      this target.
                    </strong>
                    {validation.canonicalTargetId ? (
                      <>
                        <p className={styles.dangerText}>
                          It records the source's merge chain as ending at{" "}
                          <strong>{canonicalName ?? validation.canonicalTargetId}</strong> instead.
                        </p>
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          disabled={busy !== null}
                          onClick={() => {
                            const canonicalId = validation.canonicalTargetId;
                            if (canonicalId) void runValidate(canonicalId, canonicalName);
                          }}
                        >
                          Use this company as target instead
                        </Button>
                      </>
                    ) : (
                      <p className={styles.dangerText}>
                        The source has no merge record at all — double-check you have the right
                        companies before proceeding.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {!validation.targetCompanyAlive && (
                <div className={styles.dangerBox} role="alert">
                  <Icon icon="exclamation" size="small" />
                  <div>
                    <strong>
                      The target is not a live company in the address book — moving content to a
                      dead company is almost certainly wrong.
                    </strong>
                  </div>
                </div>
              )}

              {needsAck && (
                <Checkbox
                  label="I understand this move is not backed by a merge record"
                  // The design-system Checkbox's `label` prop is a bare span,
                  // not linked to the input — the accessible name needs this.
                  aria-label="I understand this move is not backed by a merge record"
                  checked={unconfirmedAck}
                  disabled={busy !== null}
                  onChange={event => setUnconfirmedAck(event.target.checked)}
                />
              )}

              {validation.conflicts.length > 0 && (
                <section aria-label="Conflicts">
                  <h3 className={styles.sectionHeading}>
                    Conflicts ({validation.conflicts.length})
                  </h3>
                  <p className={styles.sectionNote}>
                    The target already has content in these slots. Skip (recommended) leaves the row
                    on the source. Forcing a row through does NOT fail the move — no database
                    constraint prevents it — it leaves the target with two live rows in the same
                    slot, and which one the public site serves is undefined. Only force when you
                    intend to clean up the duplicate immediately.
                  </p>
                  <ul className={styles.decisionList}>
                    {validation.conflicts.map(conflict => (
                      <li key={conflict.sourceRowId} className={styles.decisionRow}>
                        <div className={styles.decisionInfo}>
                          <span className={styles.decisionTable}>
                            {TABLE_LABELS[conflict.table]}
                          </span>
                          <span className={styles.decisionSlot}>{conflictSlot(conflict)}</span>
                          <code className={styles.uuid}>{conflict.sourceRowId}</code>
                        </div>
                        <Checkbox
                          label="Skip"
                          aria-label={`Skip row ${conflict.sourceRowId}`}
                          checked={skipDecisions[conflict.sourceRowId] ?? true}
                          disabled={busy !== null}
                          onChange={event =>
                            setSkipDecisions(prev => ({
                              ...prev,
                              [conflict.sourceRowId]: event.target.checked,
                            }))
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {validation.warnings.length > 0 && (
                <section aria-label="Warnings">
                  <h3 className={styles.sectionHeading}>Warnings ({validation.warnings.length})</h3>
                  <p className={styles.sectionNote}>
                    The target already has a lawyer profile for the same contact. Duplicates are
                    allowed, so these move by default — tick Skip to leave one behind.
                  </p>
                  <ul className={styles.decisionList}>
                    {validation.warnings.map(warning => (
                      <li key={warning.sourceRowId} className={styles.decisionRow}>
                        <div className={styles.decisionInfo}>
                          <span className={styles.decisionTable}>
                            {TABLE_LABELS[warning.table]}
                          </span>
                          <span className={styles.decisionSlot}>
                            contact {warning.contactId} already profiled at target
                          </span>
                          <code className={styles.uuid}>{warning.sourceRowId}</code>
                        </div>
                        <Checkbox
                          label="Skip"
                          aria-label={`Skip row ${warning.sourceRowId}`}
                          checked={skipDecisions[warning.sourceRowId] ?? false}
                          disabled={busy !== null}
                          onChange={event =>
                            setSkipDecisions(prev => ({
                              ...prev,
                              [warning.sourceRowId]: event.target.checked,
                            }))
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {step === "result" && applied && target && (
            <>
              <p className={styles.resultSummary}>
                Moved <strong>{applied.totalMoved}</strong> rows to <strong>{targetLabel}</strong>.
              </p>

              {applied.totalMoved > 0 && (
                <ul className={styles.countsList} aria-label="Rows moved per table">
                  {nonZeroCounts(applied.counts).map(([key, count]) => (
                    <li key={key} className={styles.countsItem}>
                      <span>{TABLE_LABELS[key]}</span>
                      <span className={styles.countBadge}>{count}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className={styles.manifestBox}>
                <p className={styles.manifestNote}>
                  The manifest below is the <strong>rollback manifest</strong> — the only record of
                  exactly which rows this run moved. Keep it: undoing this move means running it
                  again with source and target swapped, restricted to these row ids.
                </p>
                <Button variant="outlined" color="default" onClick={handleDownloadManifest}>
                  Download manifest
                </Button>
              </div>

              <Link
                to={ROUTES.profiles.firmDetail(target.id)}
                className={styles.targetLink}
                onClick={onClose}
              >
                Open the target firm's page
              </Link>
            </>
          )}

          {error && (
            <p className={styles.errorText} role="alert">
              {error}
            </p>
          )}
        </div>

        <div className={styles.modalFooter}>
          {step === "pick" && (
            <>
              <Button variant="outlined" color="default" onClick={onClose} disabled={busy !== null}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!pickedCompany || sameAsSource || busy !== null}
                onClick={() => {
                  if (pickedCompany) void runValidate(pickedCompany.id, pickedCompany.name);
                }}
              >
                {busy === "validate" ? "Validating…" : "Validate move"}
              </Button>
            </>
          )}

          {step === "review" && (
            <>
              <Button
                variant="outlined"
                color="default"
                onClick={() => {
                  setStep("pick");
                  setError(null);
                }}
                disabled={busy !== null}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={applyDisabled}
                onClick={() => void handleApply()}
              >
                {busy === "apply" ? "Applying…" : "Apply move"}
              </Button>
            </>
          )}

          {step === "result" && (
            <Button variant="contained" color="primary" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MoveCompanyContentModal;
